import { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { Route, Routes, Link, useLocation } from 'react-router-dom';
import { getHomeUrl, getLogo } from '../utils/config';
import { getUsersAdmin, getBaseUrl } from '../utils/api';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton'; // Modern MUI v5 Button
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import ViewList from '@mui/icons-material/ViewList';
import Info from '@mui/icons-material/Info';
import People from '@mui/icons-material/People';
import FitnessCenter from '@mui/icons-material/FitnessCenter';
import Email from '@mui/icons-material/Email';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import Image from '@mui/icons-material/Image';
import Create from '@mui/icons-material/Create';
import QueryBuilder from '@mui/icons-material/QueryBuilder';
import About from './about';
import Ascents from './ascents';
import Leaderboard from './leaderboard';
import Media from './media';
import ProblemEdit from './problem-edit';
import ProblemList from './problem-list';
import Users from './users';

const drawerWidth = 240;

const App = () => {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(false);
  const [title, setTitle] = useState("Problems");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  let location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (accessToken) {
      getUsersAdmin(accessToken).then((res) => { setIsAdmin(res) });
    }
  }, [accessToken]);

  useEffect(() => {
    const pathname = location.pathname;
    if (pathname==='/') {
      setTitle('Problems');
    } else if (pathname==='/ascents/0') {
      setTitle('My logbook');
    } else if (pathname.startsWith('/ascents')) {
      setTitle('Activity');
    } else if (pathname==='/leaderboard') {
      setTitle('Leaderboard');
    } else if (pathname==='/about') {
      setTitle('About');
    } else if (pathname==='/problem-edit/-1') {
      setTitle('Add problem');
    } else if (pathname==='/media') {
      setTitle('Edit media');
    } else if (pathname.length>14 && pathname.substring(0,14)==="/problem-edit/") {
      setTitle('Edit problem');
    } else if (pathname==='/users') {
      setTitle('Users');
    } else {
      console.log('Unknown route: ' + pathname);
    }
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <a href={getHomeUrl()} target="_blank" rel="noreferrer">
        <Toolbar sx={{ background: getLogo() }} />
      </a>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/" onClick={handleDrawerToggle}>
            <ListItemIcon><ViewList /></ListItemIcon>
            <ListItemText primary="Problems" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/leaderboard" onClick={handleDrawerToggle}>
            <ListItemIcon><FitnessCenter /></ListItemIcon>
            <ListItemText primary="Leaderboard" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton component={Link} to="/ascents/-1" onClick={handleDrawerToggle}>
            <ListItemIcon><People /></ListItemIcon>
            <ListItemText primary="Activity" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton component={Link} to="/about" onClick={handleDrawerToggle}>
            <ListItemIcon><Info /></ListItemIcon>
            <ListItemText primary="About" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton component="a" href="mailto:jostein.oygarden@gmail.com?subject=Training">
            <ListItemIcon><Email /></ListItemIcon>
            <ListItemText primary="Contact" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        {isAuthenticated &&
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/ascents/0" onClick={handleDrawerToggle}>
              <ListItemIcon><QueryBuilder /></ListItemIcon>
              <ListItemText primary="My logbook" />
            </ListItemButton>
          </ListItem>
        }
        {isAdmin &&
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/problem-edit/-1" onClick={handleDrawerToggle}>
              <ListItemIcon><Create /></ListItemIcon>
              <ListItemText primary="Add problem" />
            </ListItemButton>
          </ListItem>
        }
        {isAdmin &&
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/media" onClick={handleDrawerToggle}>
              <ListItemIcon><Image /></ListItemIcon>
              <ListItemText primary="Edit media" />
            </ListItemButton>
          </ListItem>
        }
        {isAdmin &&
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/users" onClick={handleDrawerToggle}>
              <ListItemIcon><SupervisorAccount /></ListItemIcon>
              <ListItemText primary="Edit users" />
            </ListItemButton>
          </ListItem>
        }
        {!isAuthenticated ?
          <ListItem disablePadding>
            <ListItemButton onClick={() => loginWithRedirect({})}>
              <ListItemIcon><AccountCircle /></ListItemIcon>
              <ListItemText primary="Sign in" />
            </ListItemButton>
          </ListItem>
        :
          <ListItem disablePadding>
            <ListItemButton onClick={() => logout({logoutParams: { returnTo: getBaseUrl()}}) }>
              <ListItemIcon><AccountCircle /></ListItemIcon>
              <ListItemText primary="Sign out" />
            </ListItemButton>
          </ListItem>
        }
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {title}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, 
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        <Routes>
          <Route path='/' element={<ProblemList/>}/>
          <Route path="/ascents/:userId" element={<Ascents/>}/>
          <Route path="/leaderboard" element={<Leaderboard/>}/>
          <Route path="/about" element={<About/>}/>
          <Route path="/media" element={<Media/>}/>
          <Route path="/problem-edit/:id" element={<ProblemEdit/>}/>
          <Route path="/users" element={<Users/>}/>
        </Routes>
      </Box>
    </Box>
  );
}

export default App;